import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './css/index.css';
import './css/spacing.css';
import './components/ui/ModernButton.css';
import { PrimeReactProvider } from "primereact/api";
import ModernApp from './screens/ModernApp';
import { ModernPrivacyPolicy } from './screens/ModernPrivacyPolicy';
import { ModernAboutUs } from './screens/ModernAboutUs';
import { ModernContact } from './screens/ModernContact';
import { ModernTermsOfService } from './screens/ModernTermsOfService';
import { ModernDownloadPage } from './pages/ModernDownloadPage';
import { SharedAssessmentPage } from './pages/SharedAssessmentPage';
import ModernFeatures from './screens/ModernFeatures';
import ModernHowItWorks from './screens/ModernHowItWorks';
import { AuthProvider } from './contexts/AuthContext';
import { RegistrationProvider } from './contexts/RegistrationContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoginPage } from './pages/agent/LoginPage';
import { DashboardPage } from './pages/agent/DashboardPage';
import { ProfilePage } from './pages/agent/ProfilePage';
import { NotificationsPage } from './pages/agent/NotificationsPage';
import { UnauthorizedPage } from './pages/agent/UnauthorizedPage';
import { DeactivatedPage } from './pages/agent/DeactivatedPage';
import { NotFoundPage, RouteErrorPage } from './pages/agent/NotFoundPage';
import RegisterPage from './pages/agent/RegisterPage';
import AgentManagementPage from './pages/agent/AgentManagementPage';
import CustomersPage from './pages/agent/CustomersPage';
import AddCustomerPage from './pages/agent/AddCustomerPage';
import CustomerDetailPage from './pages/agent/CustomerDetailPage';
import EditCustomerPage from './pages/agent/EditCustomerPage';
import InsightsPage from './pages/agent/InsightsPage';
import RecentActivitiesPage from './pages/agent/RecentActivitiesPage';
import CustomerAssignmentsPage from './pages/agent/CustomerAssignmentsPage';
import SystemSettingsPage from './pages/agent/SystemSettingsPage';
import PortalGuidePage from './pages/agent/PortalGuidePage';
import SubscriptionPage from './pages/agent/SubscriptionPage';
import GiftPurchasePage from './pages/agent/GiftPurchasePage';
import GiftSuccessPage from './pages/agent/GiftSuccessPage';
import reportWebVitals from './components/reportWebVitals';

const router = createBrowserRouter( [
    { path: '/', element: <ModernApp />, errorElement: <RouteErrorPage /> },
    { path: '/features', element: <ModernFeatures />, errorElement: <RouteErrorPage /> },
    { path: '/how-it-works', element: <ModernHowItWorks />, errorElement: <RouteErrorPage /> },
    { path: '/privacy', element: <ModernPrivacyPolicy />, errorElement: <RouteErrorPage /> },
    { path: '/about', element: <ModernAboutUs />, errorElement: <RouteErrorPage /> },
    { path: '/contact', element: <ModernContact />, errorElement: <RouteErrorPage /> },
    { path: '/terms', element: <ModernTermsOfService />, errorElement: <RouteErrorPage /> },
    { path: '/download', element: <ModernDownloadPage />, errorElement: <RouteErrorPage /> },
    { path: '/shared-assessment/:token', element: <SharedAssessmentPage />, errorElement: <RouteErrorPage /> },
    { path: '/agent/login', element: <LoginPage />, errorElement: <RouteErrorPage /> },
    { path: '/agent/register', element: <RegisterPage />, errorElement: <RouteErrorPage /> },
    { path: '/agent/unauthorized', element: <UnauthorizedPage />, errorElement: <RouteErrorPage /> },
    { path: '/agent/deactivated', element: <DeactivatedPage />, errorElement: <RouteErrorPage /> },
    {
        path: '/agent',
        element: (
            <ProtectedRoute>
                <DashboardLayout />
            </ProtectedRoute>
        ),
        errorElement: <RouteErrorPage />,
        children: [
            { path: 'dashboard', element: <DashboardPage /> },
            { path: 'profile', element: <ProfilePage /> },
            { path: 'notifications', element: <NotificationsPage /> },
            { path: 'activities', element: <RecentActivitiesPage /> },
            { path: 'customers', element: <CustomersPage /> },
            { path: 'customers/add', element: <AddCustomerPage /> },
            { path: 'customers/:customerId', element: <CustomerDetailPage /> },
            { path: 'customers/:customerId/edit', element: <EditCustomerPage /> },
            { path: 'insights', element: <InsightsPage /> },
            { path: 'guide', element: <PortalGuidePage /> },
            { path: 'subscription', element: <SubscriptionPage /> },
            { path: 'gifts', element: <GiftPurchasePage /> },
            { path: 'gifts/success', element: <GiftSuccessPage /> },
            { path: 'agents', element: <ProtectedRoute requireMasterAgent={true}><AgentManagementPage /></ProtectedRoute> },
            { path: 'admin/assignments', element: <ProtectedRoute requireMasterAgent={true}><CustomerAssignmentsPage /></ProtectedRoute> },
            { path: 'admin/settings', element: <ProtectedRoute requireSuperAgent={true}><SystemSettingsPage /></ProtectedRoute> },
            { path: '', element: <DashboardPage /> } // Default redirect to dashboard
        ]
    },
    { path: '*', element: <NotFoundPage />, errorElement: <RouteErrorPage /> } // Catch-all must stay last
] );

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <RegistrationProvider>
          <PrimeReactProvider>
            <RouterProvider router={router} />
          </PrimeReactProvider>
        </RegistrationProvider>
      </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
